/**
 * useOrgCurrency Hook - Get organization currency from localStorage
 *
 * USAGE:
 * const currency = useOrgCurrency();
 *
 * Returns the organization's currency code (e.g., 'USD', 'LKR', 'EUR')
 * Falls back to 'USD' if no currency is set
 *
 * NOTE: Currency is set during login and stored in localStorage
 * This hook provides a centralized way to access it across components
 */

import { useState, useEffect } from 'react';

export const useOrgCurrency = (): string => {
  const [currency, setCurrency] = useState<string>(() => {
    return localStorage.getItem('currency') || 'USD';
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const updatedCurrency = localStorage.getItem('currency') || 'USD';
      setCurrency(updatedCurrency);
    };

    // The native 'storage' event only fires in OTHER tabs/windows, never the
    // one that made the change — 'currencyUpdated' is dispatched alongside
    // the localStorage write (see institue/index.tsx's save handler) so this
    // tab updates immediately too, same pattern as 'orgLogoUpdated'.
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('currencyUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('currencyUpdated', handleStorageChange);
    };
  }, []);

  return currency;
};
